import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenPaths = { [level: number]: string[] };

interface NavigationContextType {
  openPaths: OpenPaths;
  setOpenPaths: Dispatch<SetStateAction<OpenPaths>>;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const [openPaths, setOpenPaths] = useState<OpenPaths>({});
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const value = useMemo(
    () => ({
      openPaths,
      setOpenPaths,
      setOpenMenu,
      isMenuOpen: openMenu,
      open: () => {
        setOpenMenu(true);
      },
      close: () => {
        setOpenMenu(false);
        setOpenPaths({});
      },
      toggle: () => {
        setOpenMenu(prev => !prev);
      }
    }),
    [openMenu, openPaths]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigationContext must be used within NavigationProvider');
  return context;
};
